import { type KeyboardEventHandler, type LegacyRef, useEffect, useState } from 'react';
import {
    FormControl,
    FormHelperText,
    FormLabel,
    Grid,
    GridItem,
    useBreakpointValue,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useEstimatedDeliveryDate } from '../../bff-api/delivery/useEstimatedDeliveryDate';
import { useLocalStorageMutation } from '../../hooks';
import { StorageKey } from '../../hooks/model/StorageKey';
import { Button, Modal, Text } from '../../ui-components';
import { QueryKey } from '../../utils/api';
import { isModernBrowser } from '../../utils/utils';
import { Field } from '../field';
import { ZIP_CODE_FIELD, useZipCodeField } from '../forms/useZipCodeField';
import type { DeliveryResponse } from '../../bff-api/delivery/model';

export const ZIP_CODE_NOT_FOUND_MESSAGE = {
    id: 'zip.code.error.not.found',
    defaultMessage: "Zip code can't be found. Please try again.",
};

interface Props {
    id: string;
    zipCode: string;
    isOpen: boolean;
    onClose: () => void;
    onChange?: (data: DeliveryResponse) => Promise<void>;
}

export const ZipCodeModal: React.FC<Props> = ({
    id,
    isOpen,
    zipCode,
    onClose,
    onChange,
}: Props) => {
    const [isLoading, setIsLoading] = useState(false);

    const intl = useIntl();
    const zipCodeMutation = useLocalStorageMutation(StorageKey.USER_POSTAL_CODE, [
        [QueryKey.USER_ADDRESS_ZIP_CODE, zipCode],
    ]);

    const form = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: { zipCode },
    });

    const {
        formState: { errors },
        trigger,
        watch,
    } = form;

    const newZipCode = watch(ZIP_CODE_FIELD);

    const { data, refetch } = useEstimatedDeliveryDate(newZipCode);

    useEffect(() => {
        if (zipCode) {
            form.setValue(ZIP_CODE_FIELD, zipCode);
        }
    }, [zipCode, form]);

    const onKeyDown: KeyboardEventHandler<HTMLInputElement> = async (e) => {
        if (e.key === 'Enter') {
            await onApplyZipCode();
        }
    };

    const { helpText, label, labelRef, ...zipCodeField } = useZipCodeField(form, onKeyDown);

    const onApplyZipCode = async (): Promise<void> => {
        const isValid = await trigger(ZIP_CODE_FIELD);

        if (!isValid) {
            return;
        }

        setIsLoading(true);

        if (newZipCode !== zipCode) {
            await zipCodeMutation.mutateAsync(newZipCode);

            // The result from the first refetch returns undefined data. It seems a bug.
            // That is why we need to call the refetch twice.
            await refetch();
            const result = await refetch();

            if (result.data?.edd) {
                if (onChange) {
                    await onChange(result.data);
                }

                form.clearErrors();
                onClose();
            } else {
                form.setError(ZIP_CODE_FIELD, {
                    type: 'custom',
                    message: intl.formatMessage(ZIP_CODE_NOT_FOUND_MESSAGE),
                });
            }
        } else if (data?.edd) {
            onClose();
        } else {
            form.setError(ZIP_CODE_FIELD, {
                type: 'custom',
                message: intl.formatMessage(ZIP_CODE_NOT_FOUND_MESSAGE),
            });
        }

        setIsLoading(false);
    };

    const variant = useBreakpointValue({
        base: isModernBrowser ? 'fullscreen' : 'fullscreen_legacy',
        md: 'ui-default',
    });

    return (
        <Modal
            id={id}
            title={intl.formatMessage({
                id: 'edit.zip.code',
                defaultMessage: 'Edit Zip Code',
            })}
            isOpen={isOpen}
            onClose={onClose}
            variant={variant}
        >
            <Text id={`${id}-description`} color="slt-pepper.1000">
                <FormattedMessage
                    id="zip.code.description"
                    defaultMessage="Enter a ZIP Code to see when it will arrive."
                />
            </Text>
            <FormControl variant={zipCodeField.variant}>
                <Grid
                    marginTop={4}
                    templateAreas={`"label label"
                                  "field cta"
                                  "helper-text helper-text"`}
                    gridTemplateColumns="auto max-content"
                    columnGap={2}
                >
                    <GridItem area="label">
                        <FormLabel
                            ref={labelRef as LegacyRef<HTMLLabelElement>}
                            htmlFor={zipCodeField.id}
                            data-testid={`${zipCodeField.id}-label`}
                            variant={zipCodeField.variant}
                        >
                            {label}
                        </FormLabel>
                    </GridItem>
                    <GridItem area="field">
                        <Field {...zipCodeField} />
                    </GridItem>
                    <GridItem area="helper-text">
                        <FormHelperText data-testid={`${zipCodeField.id}-helper-message`}>
                            {helpText}
                        </FormHelperText>
                    </GridItem>
                    <GridItem area="cta">
                        <Button
                            id={`${id}-apply-button`}
                            variant="secondary"
                            isDisabled={!!errors.zipCode}
                            isLoading={isLoading}
                            onClick={onApplyZipCode}
                        >
                            <FormattedMessage id="apply" defaultMessage="Apply" />
                        </Button>
                    </GridItem>
                </Grid>
            </FormControl>
        </Modal>
    );
};

ZipCodeModal.defaultProps = {
    onChange: undefined,
};
